import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, ElementRef, Inject, NgZone, OnDestroy, OnInit, Renderer2, ViewChild, ViewEncapsulation, DOCUMENT } from '@angular/core'
import { Router, RouterModule } from '@angular/router'
import { Address, ApplicationService, BiometricConsentGiven, DashboardItemStatus, LandingPageService } from '@idp-education/ors-test-taker-bff-client-v1'
import { DragScrollComponent, DragScrollItemDirective } from 'ngx-drag-scroll'
import { BehaviorSubject, finalize, first, forkJoin, map, Observable, switchMap, tap } from 'rxjs'
import { DateTime } from 'luxon'
import { DashboardService } from './dashboard.service'
import { CommonService } from '../../common/services/common.service'
import { AuthService } from '../../common/services/auth.service'
import { getNewTestFormat, getNewWritingFormat, getTestType } from '../../common/utilities/infer-product-name'
import { extractText } from '../../common/utilities/offline.helper'
import { StateService } from '../../common/services/state.service'
import { ApplicationsService } from '../../common/services/applications.service'
import { CallToActionService } from '../../common/services/call-to-action.service'
import { OfflinePaymentService } from '../../common/services/offline-payment-service'
import { ChangePasswordComponent } from './components/change-password/change-password.component'
import { OfflineOverlayComponent } from './components/offline-overlay/offline-overlay.component'
import { BiometricConsentOverlayComponent } from './components/biometric-consent-overlay/biometric-consent-overlay.component'
import { ToastrModule, ToastrService } from 'ngx-toastr'
import { environment } from 'src/environments/environment'
import { provideNgxMask } from 'ngx-mask'
import { GtmTrackingService } from 'src/app/services/gtm-tracking.service'
import { LoaderService } from 'src/app/services/loader.service'
import { UserAuthService } from 'src/app/services/user-auth.service'
import { SharedStateService } from 'src/app/services/shared-state.service'
import { cxDomain, PAPER_BASED } from '../../common/utilities/constants'
import { generateSesUtmParams, generateUtmUrl } from 'src/app/common/utilities/utm-params-utils'
import { NbaPod } from './dashboard.model'
import { nbaActions, PaymentStatus } from 'src/app/common/enums/enums'
import { ExpiryBadgeComponent } from '../../common/components/expiry-badge/expiry-badge.component'

type ScrollableCardType = 'unpaid' | 'offlinePending' | 'offlineConfirmed' | 'completed' | 'result'

interface ScrollableCard {
  type: ScrollableCardType
  data: any
}
@Component({
    selector: 'app-dashboard',
    imports: [CommonModule, RouterModule, ChangePasswordComponent, OfflineOverlayComponent, BiometricConsentOverlayComponent, DragScrollComponent, DragScrollItemDirective, ExpiryBadgeComponent, ToastrModule],
    providers: [provideNgxMask()],
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DashboardComponent implements OnInit, OnDestroy {
  cxDomain = cxDomain
  leftBoundReached = true // assume at start we are at left bound
  rightBoundReached = false
  openDropDown = false
  showProfileDropdown = false
  popup = false
  doesApplicationExist: boolean | null = null
  callToActions: any[] = []
  upComingTests: NbaPod[] = []
  worker: Worker | undefined
  unpaidApplication: any
  hasUnpaidTests = false
  remainingTime: string | undefined
  interval: any
  offlinePendingApplication: NbaPod[] = []
  offlinePaymentPending = false
  applicationId: any
  receiptNumber: string | undefined
  offlinePaymentConfirmed = false
  offlinePaymentConfirmedApplication: NbaPod[] = []
  applicationComplete = false
  completedApplication: NbaPod[] = []
  results: any
  hasResult = false
  eTRFUrl$: Observable<string> | any
  testLocationId: string | undefined
  paymentOptions: any
  openOverlayBlock = false
  showBiometricConsentOverlay = false
  hasBiometricConsentPending = false
  biometricConsentCard: NbaPod | null = null
  gotItButtonLabel: any
  viewInstructionLabel: any
  contentfulResponseData: any
  scrollableCards: any[] = []
  finalData: any[] = []
  receiptNumberMap: Map<string, BehaviorSubject<string>> = new Map() // Map of applicationId -> BehaviorSubject of receiptNumber
  testLocationIdMap: Map<string, BehaviorSubject<string>> = new Map()
  offlinePageData: { finalData: any[], contentfulData: any, viewInstructionLabel: any, gotItButtonLabel: any } = {
    finalData: [],
    contentfulData: null,
    viewInstructionLabel: '',
    gotItButtonLabel: ''
  }
  @ViewChild('changePasswordComponent') changePasswordComponent!: ChangePasswordComponent
  applicationStatusMap: Map<string, { status: string, hasUnknownData: boolean }> = new Map()
  isTestsAvailable = false
  userName: string | undefined
  userEmail: string | undefined
  cardIndex = 0
  @ViewChild('scrollContainerRef', { static: false, read: ElementRef })
  scrollContainerRef!: ElementRef
  userCreatedDate: any
  pageReferrer: any
  isAcademicTests: any
  essentialsData: any
  essentialsItems: any[] | undefined
  activeEssentials: any
  isMobileView = false
  appCorrelationIdMap: Map<string, string> = new Map()
  allDashboardData: any
  constructor (
    private authService: AuthService,
    private dashboardService: DashboardService,
    private callToActionService: CallToActionService,
    private applicationService: ApplicationService,
    private applicationsService: ApplicationsService,
    private offlinePaymentService: OfflinePaymentService,
    private readonly landingService: LandingPageService,
    @Inject(DOCUMENT) readonly document: any,
    private readonly renderer: Renderer2,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private commonService: CommonService,
    private stateService: StateService, private gtmService: GtmTrackingService,
    private loaderService: LoaderService,
    private userAuthService: UserAuthService,
    public sharedService: SharedStateService,
    private router: Router,
    private toast: ToastrService
    ) { }

  ngOnInit () {
    this.pageReferrer = this.userAuthService.getCookie('referrerUrl') || ''
    // Track screen size for mobile detection
    this.checkScreenSize()
    window.addEventListener('resize', () => this.checkScreenSize())
    this.getResultsData()
    this.renderer.addClass(this.document.body, 'noScroll')
    this.stateService.userDetails.pipe(first()).subscribe((userDetails: any) => {
      if (userDetails?.firstName) {
        this.userName = userDetails.firstName
        this.userEmail = userDetails.emailAddress
        this.userCreatedDate = userDetails.createdOn
      }
    })
    this.authService?.checkToken()?.pipe(first()).subscribe(() => {
      this.fetchTests()
    }, () => {
      window.location.href = generateUtmUrl(`${environment.bxURL}/account`)
    })
    this.stateService?.closePopup?.subscribe(() => {
      this.ngZone.run(() => {
        this.openOverlayBlock = false
        this.cdr.detectChanges()
      })
    })
    this.gtmService.logGaPageview('dashboard_page')
  }

  getResultsData () {
    this.loaderService.show()
    this.dashboardService?.getAllResultAvailable()?.pipe(
      first(),
      finalize(() => this.loaderService.hide())
    ).subscribe({
      next: (data: any) => {
        if (data?.length) {
          const now = new Date()
          const thirtyDaysAgo = new Date()
          thirtyDaysAgo.setDate(now.getDate() - 30)
          const filteredData = data
            .filter((item: any) => {
              const testDate = new Date(this.getTestStartDate(item))
              return testDate >= thirtyDaysAgo && testDate <= now
            })
            .sort((a: any, b: any) => {
              const dateA = new Date(this.getTestStartDate(a)).getTime()
              const dateB = new Date(this.getTestStartDate(b)).getTime()
              return dateA - dateB
            })

          this.hasResult = filteredData.length > 0
          this.results = filteredData
        }
      },
      error: (err) => {
        console.error('Error fetching results:', err)
        this.hasResult = false
        this.results = []
      }
    })
  }

  getStudentEssentials (prodName: string, country: string) {
    this.dashboardService.getStudentEssentials(prodName, country).pipe(first(), finalize(() => this.loaderService.hide())).subscribe((essentialsData) => {
      this.essentialsData = essentialsData
      this.essentialsItems = [...(essentialsData?.mediaCardsCollection?.items || [])]
    })
  }

  navigateToEssentials (item: any) {
    this.activeEssentials = item?.title
    const essentialTitle = this.commonService.formatGtmValue(item?.title)
    this.gtmService.logGaClickEvents('ielts_ses', 'dashboard_page', this.pageReferrer, essentialTitle, essentialTitle, 'ielts_ses')
    window.open(generateSesUtmParams(item?.callToAction?.primaryCtaUrl, essentialTitle))
  }

  goToExploreEssentials (contentLabel: string) {
    const exploreLabel = this.commonService.formatGtmValue(contentLabel)
    this.gtmService.logGaClickEvents('ielts_ses', 'dashboard_page', this.pageReferrer, exploreLabel, exploreLabel, 'ielts_ses')
    window.open(generateSesUtmParams(environment.studentEssentialsURL, exploreLabel))
  }

  leftBoundStat (event: boolean) {
    this.leftBoundReached = event
  }

  rightBoundStat (event: boolean) {
    this.rightBoundReached = event
  }

  moveRight (): void {
    const container = this.getScrollableContainer()
    if (!container) return

    const scrollAmount = this.getScrollAmount(container)
    container?.scrollBy({ left: scrollAmount, behavior: 'smooth' })
  }

  moveLeft (): void {
    const container = this.getScrollableContainer()
    if (!container) return

    const scrollAmount = this.getScrollAmount(container)
    container?.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
  }

  private getScrollableContainer (): HTMLElement | null {
    return this.scrollContainerRef?.nativeElement?.querySelector('.drag-scroll-content') ?? null
  }

  getScrollAmount (container: HTMLElement): number {
    const card = container.querySelector('.customcard')

    if (!(card instanceof HTMLElement)) {
      return 324
    }

    try {
      const style = typeof window?.getComputedStyle === 'function' ? window.getComputedStyle(card) : null
      const marginRight = style ? parseInt(style.marginRight || '12', 10) : 12
      return card.offsetWidth + marginRight
    } catch {
      return card.offsetWidth + 12
    }
  }

  onOpenDropDown () {
    this.openDropDown = !this.openDropDown
  }

  profileDropdown () {
    this.showProfileDropdown = !this.showProfileDropdown
  }

  popUpOpenorClose () {
    this.popup = !this.popup
  }

  navigateToPrep () {
    this.logPodInteraction('ielts_prep', 'My IELTS preparation')
    window.open(generateUtmUrl(`${environment.cxURL}/ielts-prep-hub`, '_blank'))
  }

  navigateToCommunity () {
    this.logPodInteraction('ietls_community', 'My IELTS community')
    window.open(generateUtmUrl(`${environment.cxURL}/ielts-community`, '_blank'))
  }

  private logPodInteraction (podAction: string, podLabel: string) {
    this.gtmService.logGaClickEvents('acc_dashboard_pod_interaction', 'dashboard_page', this.pageReferrer, podAction, podLabel, 'one_account_dashboard', undefined, this.userEmail)
  }

  paymentCompleteClose (card: any): void {
    this.scrollableCards = this.scrollableCards.filter(c => c.data.applicationId !== card.data.applicationId)
  }

  fetchTests () {
    this.loaderService.show()

    this.dashboardService?.getAllDashboardApplications(false)?.pipe(
      finalize(() => this.loaderService.hide())
    ).subscribe((dashboardData: any) => {
      this.allDashboardData = dashboardData
      const getmergedAllApplications = [...(dashboardData?.pastTests || []), ...(dashboardData?.upcomingTests || [])]
      this.appCorrelationIdMap = new Map(getmergedAllApplications.map(item => [item.applicationId, item?.applicationCorrelationId]))
    })

    this.dashboardService.dashboardApplications$?.pipe(
      first(),
      finalize(() => this.loaderService.hide())
    ).subscribe(() => {
      this.isThereAnyApplication()
      this.getUpcomingTest()
    })
  }

  isThereAnyApplication () {
    this.loaderService.show()

    this.dashboardService?.isThereUpcomingTests()?.pipe(
      finalize(() => this.loaderService.hide())
    ).subscribe(data => {
      this.doesApplicationExist = data
    })

    this.stateService.setLatestPageUrl(null)
  }

  getUpcomingTest () {
    this.loaderService.show()
    this.dashboardService.getDashboard(this.userCreatedDate).pipe(
      finalize(() => this.loaderService.hide())
    ).subscribe(upcomingTest => {
      this.upComingTests = upcomingTest
      this.scrollableCards = this.getLrwSpeakingDate(this.upComingTests)
      this.loadOfflineExpiries()
      this.isTestsAvailable = this.checkUpcomingTests(this.upComingTests)
      this.unpaidApplication = this.findUnpaidApplication()
      this.callToActions = this.addCallActions()
      this.hasUnpaidTests = this.checkForUnpaidTests()
      this.isAcademicTests = this.checkForAcademicTests()
      this.hasBiometricConsentPending = this.checkBiometricConsent(upcomingTest)
      if (this.hasUnpaidTests && this.unpaidApplication) {
        this.startTimer(this.unpaidApplication?.bookingDetails?.expiryDateTimeSeconds)
      }
      this.offlinePaymentPending = this.processOfflinePaymentInProgress()
      this.offlinePaymentConfirmed = this.processOfflinePaymentConfirmed()
      this.processCompletedApplications().then(() => {
        // this.applicationComplete = !!this.completedApplication?.length
        this.buildScrollableCards() // wait until all async data is ready
      })
      if (this.isAcademicTests) {
        const testLocation = this.isAcademicTests?.bookingDetails?.testLocation?.testLocationAddresses[0]?.countryCode
        this.getStudentEssentials(this.isAcademicTests?.bookingDetails?.bookableProduct, testLocation)
      }
    })
  }

  checkUpcomingTests (upcomingTests: any) {
    let isConditionMet = false
    if (Array.isArray(upcomingTests)) {
      for (const test of upcomingTests) {
        if (
          (test.status === DashboardItemStatus.UNPAID ||
            test.status === DashboardItemStatus.OFFLINEPAYMENTINPROGRESS ||
            test.status === DashboardItemStatus.OFFLINEPAYMENTCONFIRMED ||
            test.status === DashboardItemStatus.COMPLETED) ||
          this.hasResult
        ) {
          isConditionMet = true
          break
        }
      }
    }
    return isConditionMet
  }

  findUnpaidApplication () {
    return this.upComingTests.find((app: any) => app.bookingDetails?.paymentStatus?.toUpperCase() === DashboardItemStatus.UNPAID)
  }

  addCallActions () {
    return this.callToActionService.addToCallAction(this.upComingTests)
  }

  checkForUnpaidTests () {
    return this.upComingTests.some((t: any) => t?.bookingDetails?.paymentStatus?.toUpperCase() === DashboardItemStatus.UNPAID)
  }

  checkForAcademicTests () {
    const appliedCountries = new Set(['SGP', 'ARE', 'AUS', 'BGD', 'KHM', 'EGY', 'GHA', 'HKG', 'IDN', 'KEN', 'MYS', 'MUS', 'NZL', 'NGA', 'OMN', 'PAK', 'PHL', 'SAU', 'KOR', 'LKA', 'TWN', 'THA', 'TUR', 'VNM'])
    return this.upComingTests.find((t: any) => {
      const countryCode = t?.bookingDetails?.testLocation?.testLocationAddresses[0]?.countryCode
      return appliedCountries.has(countryCode) && t?.bookingDetails?.bookableProduct?.toLowerCase().includes('academic')
    })
  }

  processOfflinePaymentInProgress () {
    const offlinePaymentInProgressApplications = this.upComingTests.filter((t: any) => t.status === DashboardItemStatus.OFFLINEPAYMENTINPROGRESS && t?.bookingDetails?.lrwStartDateTimeUtc)
      .sort((a: any, b: any) => new Date(a.lrwStartDateTimeUtc).getTime() - new Date(b.lrwStartDateTimeUtc).getTime())

    if (offlinePaymentInProgressApplications?.length) {
      offlinePaymentInProgressApplications.forEach((app: any) => {
        this.getApplicationIdDetails(app.applicationId)
      })
      this.offlinePendingApplication = offlinePaymentInProgressApplications
    }
    return !!offlinePaymentInProgressApplications?.length
  }

  processOfflinePaymentConfirmed () {
    const offlinePaymentConfirmedApplications = this.upComingTests
      .filter(t => t?.bookingDetails?.paymentStatus === DashboardItemStatus.OFFLINEPAYMENTCONFIRMED && t?.bookingDetails?.lrwStartDateTimeUtc)
      .sort((a, b) => {
        const dateA = a?.bookingDetails?.lrwStartDateTimeUtc ? new Date(a.bookingDetails.lrwStartDateTimeUtc).getTime() : 0
        const dateB = b?.bookingDetails?.lrwStartDateTimeUtc ? new Date(b.bookingDetails.lrwStartDateTimeUtc).getTime() : 0
        return dateA - dateB
      })
    this.offlinePaymentConfirmedApplication = offlinePaymentConfirmedApplications
    return !!offlinePaymentConfirmedApplications?.length
  }

  isApplicationHasUnknownData (applicationId: string, applicationStatusMap: Map<string, { status: string, hasUnknownData: boolean }>): boolean {
    return applicationStatusMap.get(applicationId)?.hasUnknownData === true
  }

  processCompletedApplications (): Promise<void> {
    return new Promise((resolve, reject) => {
      const completedApplications = this.upComingTests
        ?.filter((t: any) =>
          (t.status === DashboardItemStatus.COMPLETED || t.status === DashboardItemStatus.PAIDBUTINCOMPLETE) &&
          t.bookingDetails.lrwStartDateTimeUtc
        )
        .sort((a: any, b: any) =>
          new Date(a.lrwStartDateTimeUtc).getTime() - new Date(b.lrwStartDateTimeUtc).getTime()
        )

      this.completedApplication = completedApplications || []

      if (!completedApplications?.length) {
        resolve()
        return
      }

      this.loaderService.show()

      const applicationStatusMap = new Map<string, { status: string, hasUnknownData: boolean }>()

      const requests = completedApplications.map(application =>
        this.applicationService
          .getApplication(application?.bookingDetails?.applicationId, this.commonService.getUserUuid())
          ?.pipe(
            first(),
            map(appData => {
              if (appData) {
                const hasUnknownData = this.processApplicationData(
                  appData.userProfile,
                  appData.userProfile?.identityDetails
                )
                applicationStatusMap.set(application.bookingDetails?.applicationId, {
                  status: application?.bookingDetails?.paymentStatus,
                  hasUnknownData
                })
              }
            })
          )
      )

      forkJoin(requests)
        .pipe(
          finalize(() => {
            this.applicationStatusMap = applicationStatusMap
            this.loaderService.hide()
          })
        )
        .subscribe({
          next: () => {
            this.completedApplication = this.completedApplication.filter(app =>
              this.isApplicationHasUnknownData(app?.bookingDetails?.applicationId, applicationStatusMap)
            )

            this.buildScrollableCards()
            resolve()
          },
          error: err => {
            console.error('Error processing completed applications:', err)
            reject(err)
          }
        })
    })
  }

  buildScrollableCards () {
    const cards: any[] = []

    if (this.hasUnpaidTests && this.unpaidApplication) {
      cards.push({ type: 'unpaid', data: this.unpaidApplication })
    }

    if (this.offlinePaymentPending) {
      this.offlinePendingApplication?.forEach(app =>
        cards.push({ type: 'offlinePending', data: app })
      )
    }

    if (this.offlinePaymentConfirmed) {
      this.offlinePaymentConfirmedApplication?.forEach(app =>
        cards.push({ type: 'offlineConfirmed', data: app })
      )
    }

    this.completedApplication?.forEach(app =>
      cards.push({ type: 'completed', data: app })
    )

    if (this.hasResult) {
      this.results?.forEach((app: any) =>
        cards.push({ type: 'result', data: app })
      )
    }
  }

  private isUnknownValue (value?: string): boolean {
    return (
      typeof value === 'string' &&
      (value.trim().toLowerCase() === 'unknown' || value.trim() === '')
    )
  }

  hasInvalidS3Url (applicationData: any): boolean {
    const s3Url = applicationData?.s3Url
    return !s3Url || s3Url === environment?.s3IdImageUrl
  }

  processApplicationData (userProfile: any, identityDetails?: any): boolean {
    const unknownIds = [
      '63999c7c-cdaa-304e-71da-3c1790cdd95d', // nationalityId unknown
      '99fcace5-4689-004d-d34f-81799f15b154', // languageId unknown
      '29e7b37e-08ac-3b29-7477-fb12552762a9', // countryId unknown
      '28c6141c-c959-b0b8-cd23-4e2ef48f231c', // countryApplyingToId unknown
      'b84b2d3e-1bf4-fc79-d3d5-d6e0ceccc547', // occupationLevelId unknown
      '10375967-22c8-74f3-d8cc-2fd66e21b32e', // occupationSectorId unknown
      'e388ddb7-9c29-ed03-e91e-042be58be60b' // testReasonId unknown
    ]

    const addr = userProfile?.addressDetails || {}
    const marketing = userProfile?.marketingDetails || {}
    // Check 'Other' string fields
    const hasUnknownOtherFields = [
      userProfile.languageOther,
      marketing.countryApplyingToOther,
      marketing.currentlyStudyingEnglishAt,
      marketing.occupationLevelOther,
      marketing.occupationSectorOther,
      marketing.testReasonOther,
      addr.streetAddress1,
      addr.city
    ]
      .filter(val => val !== undefined && val !== null)
      .some(val => this.isUnknownValue(val))

    // Check unknown IDs
    const idFields = [
      userProfile.nationalityId,
      userProfile.languageId,
      addr.countryId,
      marketing.countryApplyingToId,
      marketing.occupationLevelId,
      marketing.occupationSectorId,
      marketing.testReasonId
    ]

    const hasUnknownIds = idFields.some(id => typeof id === 'string' && unknownIds.includes(id))

    // Check S3 URL validity
    const hasInvalidUrls = this.hasInvalidS3Url(identityDetails)

    return hasUnknownOtherFields || hasUnknownIds || hasInvalidUrls
  }

  getApplicationIdDetails (applicationId: string) {
    if (applicationId) {
      this.applicationService?.getApplication(applicationId)?.pipe(first()).subscribe((application) => {
        if (application?.applicationPayments?.length) {
          const receiptNumber: any = application?.applicationPayments[0]?.receiptNumber

          if (!this.receiptNumberMap?.has(applicationId)) {
            this.receiptNumberMap?.set(applicationId, new BehaviorSubject(receiptNumber))
          } else {
            this.receiptNumberMap?.get(applicationId)?.next(receiptNumber)
          }

          const testLocationId = application?.bookings[0]?.testLocationId

          if (!this.testLocationIdMap?.has(applicationId)) {
            this.testLocationIdMap?.set(applicationId, new BehaviorSubject(testLocationId))
          } else {
            this.testLocationIdMap?.get(applicationId)?.next(testLocationId)
          }
        }
      })
    }
  }

  /**
   * Fetches the OFFLINE payment method for the test location, derives the offline payment expiry
   * ({ expirationMinutes, mode }) and stores the calculated expiry date on the matching card.
   * Mirrors the calculation used in the test-taker (TT) app so both show the same due date/time.
   */
  /**
   * Loads the offline payment expiry for every rendered card that shows the offline expiry badge
   * (paymentMethod === 'OFFLINE' && paymentStatus === 'IN_PROGRESS'). This matches the badge's own
   * display condition rather than the top-level dashboard status, so the calculation runs for the
   * same bookings that show the badge.
   */
  loadOfflineExpiries (): void {
    const offlineCards = (this.scrollableCards || []).filter((c: any) =>
      c?.bookingDetails?.applicationId &&
      c?.bookingDetails?.paymentMethod === 'OFFLINE' &&
      c?.bookingDetails?.paymentStatus === 'IN_PROGRESS'
    )
    const seen = new Set<string>()
    offlineCards.forEach((c: any) => {
      const applicationId = c.bookingDetails.applicationId
      if (seen.has(applicationId)) {
        return
      }
      seen.add(applicationId)
      this.setOfflineExpiry(applicationId)
    })
  }

  setOfflineExpiry (applicationId: string): void {
    if (!applicationId) {
      return
    }
    // Show a loader on the matching cards until the async calculation resolves, so the badge does
    // not briefly flash a raw/fallback date before the correct value is computed.
    this.setOfflineExpiryLoading(applicationId, true)
    this.applicationService.getApplication(applicationId, this.commonService.getUserUuid()).pipe(first()).subscribe({
      next: (application: any) => {
        const testLocationId = application?.bookings?.[0]?.testLocationId
        if (!testLocationId) {
          this.setOfflineExpiryLoading(applicationId, false)
          return
        }
        this.offlinePaymentService.getOfflinePaymentMethod(testLocationId).pipe(first()).subscribe({
          next: (offlinePaymentMethod: any) => {
            const paymentExpiry = offlinePaymentMethod?.paymentMethodConfig?.document?.paymentMethodConfiguration?.paymentExpiry
            const offlineExpiry = paymentExpiry?.find((e: any) => e.paymentMethod === 'OFFLINE')
            const expiryDate = this.calculateOfflineExpiry(offlineExpiry, application)
            // The template renders scrollableCards, so set the outcome on those cards.
            // When there is no valid offline expiry (past deadline, NONE mode, or deadline on/after
            // the test), mark the card so the badge is hidden instead of falling back to the raw date.
            this.scrollableCards
              ?.filter((c: any) => c?.bookingDetails?.applicationId === applicationId)
              .forEach((c: any) => {
                c.bookingDetails.offlineExpiryDate = expiryDate ?? null
                c.bookingDetails.offlineExpiryUnavailable = !expiryDate
                c.bookingDetails.offlineExpiryLoading = false
              })
            this.cdr.detectChanges()
          },
          error: () => { this.setOfflineExpiryLoading(applicationId, false) }
        })
      },
      error: () => { this.setOfflineExpiryLoading(applicationId, false) }
    })
  }

  private setOfflineExpiryLoading (applicationId: string, loading: boolean): void {
    this.scrollableCards
      ?.filter((c: any) => c?.bookingDetails?.applicationId === applicationId)
      .forEach((c: any) => { c.bookingDetails.offlineExpiryLoading = loading })
  }

  /**
   * Calculates the offline payment expiry date from the OFFLINE paymentExpiry config and application.
   * TEST_DATE  -> expiry = test start date - expirationMinutes
   * BOOKING_DATE (default) -> expiry = application creation date + expirationMinutes
   * NONE -> no automatic expiry (no date to display).
   *
   * Returns null (so no badge is shown) when:
   *  - mode is NONE (no deadline),
   *  - the computed deadline has already elapsed, or
   *  - the deadline is on/after the LRW test start (offline payment is not meaningful then).
   */
  calculateOfflineExpiry (offlineExpiry: any, app: any): Date | null {
    const mode = offlineExpiry?.mode

    // NONE mode: no automatic expiry is applied; there is no deadline to display.
    if (mode === 'NONE') {
      return null
    }

    if (!offlineExpiry?.expirationMinutes) {
      return null
    }
    const expirationMinutes = offlineExpiry.expirationMinutes

    // Resolve the LRW test start (TEST_DATE anchor and the upper bound the expiry must fall before).
    // The offline deadline is anchored to the LRW test; do NOT use the speaking date.
    const lrwBooking = app?.bookings?.find((b: any) => !b.isSpeaking) || app?.bookings?.[0]
    const testStartRaw = lrwBooking?.startDateTimeUtc ?? app?.lrwStartDateTimeUtc
    const testStartMs = testStartRaw ? new Date(testStartRaw).getTime() : null

    let expiry: Date | null = null

    if (mode === 'TEST_DATE') {
      if (testStartMs === null) {
        return null
      }
      expiry = new Date(testStartMs - expirationMinutes * 60 * 1000)
    } else {
      const bookingDate = app?.createdOn ?? app?.applicationCreationDateTimeUtc
      if (!bookingDate) {
        return null
      }
      expiry = new Date(new Date(bookingDate).getTime() + expirationMinutes * 60 * 1000)
    }

    // Deadline already elapsed -> no offline expiry to show.
    if (expiry.getTime() <= Date.now()) {
      return null
    }

    // The deadline must fall strictly before the LRW test start.
    if (testStartMs !== null && expiry.getTime() >= testStartMs) {
      return null
    }

    return expiry
  }

  loadPaymentOptions (testLocationId: string) {
    this.offlinePaymentService
      .getOfflinePaymentMethod(testLocationId)
      .pipe(
        first(),
        tap((offlinePaymentMethod) => {
          if (Array.isArray(offlinePaymentMethod)) {
            this.paymentOptions = offlinePaymentMethod
            return
          }
          const {
            paymentMethod: { paymentMethodContents }
          } = offlinePaymentMethod
          if (paymentMethodContents?.length && paymentMethodContents[0]?.contents?.length) {
            const firstContent = paymentMethodContents[0]?.contents[0]
            if (firstContent) {
              this.paymentOptions = firstContent
              this.getContentfulData()
            }
          }
        })
      )
      .subscribe()
  }

  getContentfulData () {
    this.landingService?.getContentfulData('en-GB', '/offlinepayment')?.subscribe({
      next: (contentfulResponse: any) => {
        this.getButtonLabel(contentfulResponse.buttons)
        this.getLabelDetails(contentfulResponse?.labels)
        if (contentfulResponse) {
          this.compareContentFulAndBackoffice(contentfulResponse, this.paymentOptions)
        }
      }
    })
  }

  getButtonLabel (buttonLabel: any) {
    buttonLabel?.forEach((label: any) => {
      if (label.options === 'CLOSE_BUTTON') {
        this.gotItButtonLabel = label.displayText
      }
    })
  }

  getLabelDetails (labelData: any) {
    labelData?.forEach((label: any) => {
      if (label.options === 'SHOW_MORE_LABEL') {
        this.viewInstructionLabel = label.displayText
      }
    })
  }

  compareContentFulAndBackoffice (contentfulResponse: any, paymentOptions: any) {
    this.contentfulResponseData = contentfulResponse
    this.finalData = []
    contentfulResponse?.navCards?.forEach((cfData: any) => {
      paymentOptions?.paymentOptionContents?.forEach((paymentData: any) => {
        // Compare cfData.name with paymentData (e.g., title, or any other property)
        if (cfData.name.toLowerCase().includes(paymentData.title.toLowerCase())) {
          const contentfulData = new ContentfulData()
          contentfulData.overlayTitle = cfData.description
          contentfulData.description = extractText(paymentData.description)
          contentfulData.instructions = paymentData.instructions
          contentfulData.image = cfData.images
          contentfulData.label = extractText(paymentData.label)
          contentfulData.name = cfData.name
          // Use title from paymentData or cfData based on the condition
          if (paymentData.title.toLowerCase().includes('other')) {
            contentfulData.title = extractText(paymentData.label)
          } else {
            contentfulData.title = cfData.title
          }

          this.finalData.push(contentfulData)
          const offlineConfirmation = {
            finalData: this.finalData,
            contentfulData: this.contentfulResponseData,
            viewInstructionLabel: this.viewInstructionLabel,
            gotItButtonLabel: this.gotItButtonLabel
          }
          this.offlinePageData = offlineConfirmation
        }
      })
    })
  }

  openOverlay (bookingDetails: any) {
    this.receiptNumber = bookingDetails?.receiptNumber
    this.testLocationId = bookingDetails?.testLocation?.id
    if (this.testLocationId) {
      this.loadPaymentOptions(this.testLocationId)
    }
    this.openOverlayBlock = true
  }

  startTimer (secondsToPay: any) {
    this.updateRemainingTime(secondsToPay)
    this.interval = setInterval(() => {
      if (secondsToPay > 0) {
        secondsToPay--
        this.updateRemainingTime(secondsToPay)
      } else {
        clearInterval(this.interval)
      }
    }, 1000)
  }

  getTestType (bookableProductName: string) {
    return getTestType(bookableProductName)
  }

  getTestFormat (bookableProductFormat: any) {
    return getNewTestFormat(bookableProductFormat[0]?.testFormat)
  }

  getWritingFormat (bookableProductFormat: any) {
    return getNewWritingFormat(bookableProductFormat)
  }

  getAddressFormat (addresses: Address[]) {
    const physicalAddress = addresses?.find(a => a.addressType === 'PHY')
    if (physicalAddress) {
      const append = (info?: string, prefix = ''): string => info ? `${prefix} ${info}` : ''
      const addressLines = physicalAddress.addressLine1 +
        append(physicalAddress.addressLine2) +
        append(physicalAddress.addressLine3) +
        append(physicalAddress.addressLine4) +
        append(physicalAddress.city, ',') +
        append(physicalAddress.zipPostCode)
      return addressLines
    } else {
      return ''
    }
  }

  updateRemainingTime (secondsToPay: any, SecondsBeReduced = 0) {
    const timeLeft = secondsToPay - SecondsBeReduced
    this.remainingTime =
      timeLeft > 0 ? this.formatTime(timeLeft) : '0 sec'
  }

  formatTime (seconds: number): string {
    if (!seconds || seconds <= 0) {
      return '0 sec'
    }
    // Convert to minutes if seconds >= 60
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60)
      return `${minutes} min`
    }
    // Return seconds if less than 60
    return `${seconds} sec`
  }

  secondaryCtaAction (event: Event, appData: any) {
    this.gtmService.logGaClickEvents('acc_nba_interaction', 'dashboard_page', this.pageReferrer, this.commonService.formatGtmValue(appData?.nbaPod?.title), this.commonService.formatGtmValue(appData?.nbaPod?.callToActionCta?.secondaryCtaLabel), 'one_account_dashboard')
    event.preventDefault()
    if (appData?.nbaPod?.callToActionCta?.secondaryCtaLabel.toLowerCase() === 'already paid?') {
      this.popUpOpenorClose()
    } else {
      this.downloadURL(appData?.bookingDetails?.applicationId)
    }
  }

  isBiometricConsentCard (card: NbaPod): boolean {
    return card?.nbaActionCategory?.title?.toLowerCase() === nbaActions.COMPLETE_BIOMETRIC_CONSENT.toLowerCase()
  }

  openBiometricConsentOverlay (event: Event, card: NbaPod) {
    event?.preventDefault()
    this.biometricConsentCard = card
    this.gtmService.logGaClickEvents('acc_nba_interaction', 'dashboard_page', this.pageReferrer, this.commonService.formatGtmValue(card?.nbaPod?.title), this.commonService.formatGtmValue(card?.nbaPod?.callToActionCta?.primaryCtaLabel), 'one_account_dashboard')
    this.showBiometricConsentOverlay = true
  }

  closeBiometricConsentOverlay () {
    this.showBiometricConsentOverlay = false
    this.biometricConsentCard = null
  }

  confirmBiometricConsent () {
    const applicationId = this.biometricConsentCard?.bookingDetails?.applicationId
    if (!applicationId) {
      this.closeBiometricConsentOverlay()
      return
    }
    const correlationId = this.appCorrelationIdMap.get(applicationId) || this.commonService.appCorrelationId()
    this.loaderService.show()
    this.applicationService.getApplication(applicationId, correlationId).pipe(
      switchMap((response: any) => {
        const updateBiometricConsentDetailsRequest = {
          consentGiven: BiometricConsentGiven.YES,
          version: response?.version
        }
        return this.applicationService.updateBiometricConsentDetails(correlationId, applicationId, updateBiometricConsentDetailsRequest)
      }),
      first(),
      finalize(() => this.loaderService.hide())
    ).subscribe({
      next: () => {
        this.closeBiometricConsentOverlay()
        this.getUpcomingTest()
      },
      error: () => {
        this.closeBiometricConsentOverlay()
        this.toast.error($localize`Something went wrong. Please try again.`)
      }
    })
  }

  navigateToBooking (event: Event, data: any, url: any) {
    event.preventDefault()
    this.gtmService.logGaClickEvents('acc_nba_interaction', 'dashboard_page', this.pageReferrer, data?.nbaPod?.title?.toLowerCase().replace(/ /g, '_'), data?.nbaPod?.callToActionCta?.primaryCtaLabel?.toLowerCase().replace(/ /g, '_'), 'one_account_dashboard')
    if (data?.bookingDetails?.paymentStatus === PaymentStatus.IN_PROGRESS) {
      this.openOverlay(data?.bookingDetails)
    } else {
    this.userAuthService.setCookie('applicationDetails', JSON.stringify(data?.bookingDetails))
      this.userAuthService.setCookie('isCompleteBooking', 'true')
      this.userAuthService.setCookie('isFromDashboard', 'true')
      const prodName = data?.bookingDetails?.bookableProduct?.toUpperCase()
      if (prodName?.includes('OSR')) {
        this.userAuthService.setCookie('isOSR', 'true')
      }
      if (data?.bookingDetails?.bookingLines[0]?.testFormat?.toLowerCase().includes('online') && data?.bookingDetails?.paymentStatus == PaymentStatus.UNPAID) {
         window.location.href = generateUtmUrl(data?.nbaPod?.callToActionCta?.secondaryCtaUrl)
      } else {
        window.location.href = generateUtmUrl(url)
      }
    }
  }

  getTestSkillLabel (productName: string, lrwDate?: string, speakingDate?: string): string {
    const isValidDate = (dateStr?: string): boolean => {
      return !!dateStr && !dateStr.startsWith('0001-01-01')
    }

    if (productName?.includes('OSR')) {
      if (isValidDate(lrwDate)) return 'LRW'
      if (isValidDate(speakingDate)) return 'Speaking'
    }

    return 'Written test'
  }

  getTestStartDate (resVal: any): string {
    return this.commonService.getTestDate(resVal)
  }

  goToMyTests (event: Event): void {
    event.preventDefault()

    this.userAuthService.setCookie('currentTestPage', 'my-test')

    // Add a short delay to ensure cookie is stored before redirect
    setTimeout(() => {
      window.location.href = generateUtmUrl(this.commonService.myTestsRedirectionUrl())
    }, 50)
  }

  getURL (appId: string): Observable<string | any> {
    return this.applicationsService.getPDFUrl(appId)
  }

  downloadURL (appId: any) {
    this.eTRFUrl$ = this.getURL(appId)
    if (this.eTRFUrl$) {
      this.eTRFUrl$.subscribe((url: any) => {
        if (url) {
          const anchor = document.createElement('a')
          anchor.href = url
          anchor.click()
        }
      })
    }
  }

  getFormattedDate (dateVal: any, timeZoneVal: any) {
    // Parse the UTC date and convert it to the target timezone (e.g., Australia/Melbourne)
    const dateInTargetTimezone = DateTime.fromISO(dateVal, { zone: 'utc' })
      .setZone(timeZoneVal) // Convert to the target timezone
      .toFormat('dd/MM/yyyy') // Format the date in dd/MM/yyyy format

    return dateInTargetTimezone
  }

  getSkillFormattedDate (lrw: string, speaking: string, timeZone: string): string | null {
  if (this.isValidDate(lrw)) {
    return this.getFormattedDate(lrw, timeZone)
  } else if (this.isValidDate(speaking)) {
    return this.getFormattedDate(speaking, timeZone)
  } else {
    return null
  }
}

getFormattedTime (timeVal: string, timeZoneVal: string): string {
  return DateTime.fromISO(timeVal, { zone: 'utc' })
    .setZone(timeZoneVal)
    .toFormat('hh:mm a')
}

isValidDate (dateStr: string): boolean {
    return !!dateStr && !dateStr.startsWith('0001-01-01')
}
getSkillFormattedTime (lrw: string, speaking: string, timeZone: string, country?: string): string | null {
  if (this.isValidDate(lrw)) {
    return this.commonService.is24HourCountry(this.getFormattedTime(lrw, timeZone), country)
  } else if (this.isValidDate(speaking)) {
    return this.commonService.is24HourCountry(this.getFormattedTime(speaking, timeZone), country)
  } else {
    return null
  }
}

  reviewApplication (application: any, event: Event) {
    event.preventDefault()
    this.userAuthService.setCookie('applicationDetails', JSON.stringify(application))
    this.userAuthService.setCookie('currentTestPage', JSON.stringify('update-application-details'))
    window.location.href = generateUtmUrl(this.commonService.myTestsRedirectionUrl())
  }

  getLrwSpeakingDate (upcomingTests: any) {
    return upcomingTests.map((item: { bookingDetails: any }) => {
      const bookingLines = item.bookingDetails.bookingLines

      const lrwLine = bookingLines.find((line: { languageSkill: any[] }) =>
        line.languageSkill.some(skill => ['L', 'R', 'W'].includes(skill))
      )

      const speakLine = bookingLines.find((line: { languageSkill: string | string[] }) =>
        line.languageSkill.includes('S')
      )

      return {
        ...item,
        bookingDetails: {
          ...item.bookingDetails,
          lrwStartDateTimeUtc: lrwLine ? lrwLine.startDateTimeUtc : null,
          speakingStartDateTimeUtc: speakLine ? speakLine.startDateTimeUtc : null,
          imageIcon: this.getImagePath(item?.bookingDetails?.bookingLines),
          showReceiptNumber: this.checkDisplayReceiptNumber(item),
          applicationCorrelationId: this.appCorrelationIdMap.get(item?.bookingDetails?.applicationId)
        }
      }
    })
  }

  checkBiometricConsent (upcomingTests: NbaPod[]): boolean {
    return upcomingTests?.some((item: NbaPod) => this.isBiometricConsentCard(item)) ?? false
  }

  checkDisplayReceiptNumber (status: any) {
    return status?.bookingDetails?.paymentStatus === PaymentStatus.IN_PROGRESS
  }

  getImagePath (bookingLines: any) {
    const testFormat = this.getTestFormat(bookingLines)
    if (testFormat === 'IELTS on computer') {
      return '/assets/images/on-computer.svg'
    } else if (testFormat === 'Online') {
      return '/assets/images/online-home.svg'
    } else {
      return '/assets/images/onpaper-gray.svg'
    }
  }

  getWritingFormatImagePath (bookingLines: any) {
    if(bookingLines[0]?.testFormat === PAPER_BASED) {
      return 'assets/images/writingOnPaper.svg'
    }
    return '/assets/images/on-computer.svg'
  }

  checkScreenSize (): void {
    this.isMobileView = window.innerWidth <= 480
  }

  ngOnDestroy (): void {
    this.renderer.removeClass(this.document.body, 'noScroll')
  }

  openProfilePage () {
    this.logProfileInteraction('my_profile', 'My profile')
    this.showProfileDropdown = false
    window.location.href = generateUtmUrl(`${environment.domainUrl}/my-profile`)
  }

  openChangePassword () {
    this.showProfileDropdown = false
    this.router.navigate(['/account-settings'])
  }

  signOut () {
    this.logProfileInteraction('log_out', 'Log out')
    this.sharedService.clearLocalStorage()
    this.sharedService.clearState()
    this.showProfileDropdown = false
    this.authService?.signOut()
  }

  private logProfileInteraction (action: string, label: string) {
    this.gtmService.logGaClickEvents('acc_dashboard_profile_interaction', 'dashboard_page', this.pageReferrer, action, label, 'one_account_dashboard', undefined, this.userEmail)
  }

  navigateToMyTest () {
    this.logPodInteraction('test_results', 'My tests and results')
    this.userAuthService.setCookie('currentTestPage', 'my-test')
    setTimeout(() => {
      if (this.allDashboardData?.upcomingTests?.some((test: any) => test.productName?.toLowerCase()?.includes('online'))) {
        window.location.href = generateUtmUrl(`${environment.bxURL}/my-tests`)
      } else {
        window.location.href = generateUtmUrl(this.commonService.myTestsRedirectionUrl())
      }
    }, 2000)
  }

  goToBooking () {
    this.gtmService.logGaClickEvents('acc_book_now_cta', 'dashboard_page', this.pageReferrer, 'cta_click', 'Book now', 'one_account_dashboard', undefined, this.userEmail)
    window.open(generateUtmUrl(`${environment.bxSearchURL}`), '_blank')
  }
  
   openAccountSettings () {
    this.logProfileInteraction('account_settings', 'Account settings')
    this.router.navigate(['/account-settings'])
  }
}

class ContentfulData {
  overlayTitle: string | undefined
  image: object | undefined
  name: string | undefined
  title: string | undefined
  description: string | undefined
  instructions: string | undefined
  label: string | undefined
}
